import { useContext } from 'react';
import { useSelector } from 'react-redux';
import * as xlsx from 'xlsx';
import { gql, useMutation } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';

import { FlexEnd } from '../../../commonStyles';
import { useAppDispatch } from '../../../app/hooks';
import { handleModal } from '../../../features/modal/modalSlice';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../features/snackbar/snackbarSlice';
import { PriceGridContext } from '../../../context/PriceGridContext';
import { selectProject } from '../../../features/project/projectSlice';
import { selectUser } from '../../../features/auth/authSlice';
import { IUnit } from '../../../types/unit';
import { grossPriceFromNetHST, numToCurrency, getOutdoorSize, getOutdoorType } from '../../../utils/Functions';

const ALLOWED_HEADERS = ['suite', 'unitType', 'modelType', 'size', 'outdoorSize', 'exposure', 'basePrice', 'bathroom', 'rental', 'netHstPrice'];
const ALLOWED_FILE_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

const hasValue = (value: any) => value !== undefined && value !== null && value !== '';

const UploadExcel = () => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const { setModalType, units, setUnits } = useContext(PriceGridContext);

  const handleClose = () => {
    setModalType('');
    storeDispatch(handleModal(false));
  };

  const [updateUnits, { loading }] = useMutation(UPDATEUNITS, {
    onCompleted: (data) => {
      const updatedUnits = data.unitUpdateManyInfo;
      // The mutation only returns the fields we selected, not the full unit shape (e.g. no
      // suite/level/allocation), so merge onto the existing unit rather than replacing it outright.
      setUnits(
        units.map((unit: IUnit) => {
          const updated = updatedUnits.find((updatedUnit: any) => updatedUnit._id === unit._id);
          return updated ? { ...unit, ...updated } : unit;
        })
      );
      storeDispatch(showSuccessSnackbar(`${updatedUnits.length} unit(s) updated`));
      handleClose();
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar('Error updating units'));
      console.log(err, 'err');
    },
  });

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.target.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    if (!ALLOWED_FILE_EXTENSIONS.some((extension) => lowerName.endsWith(extension))) {
      storeDispatch(showErrorSnackbar('Only Excel (.xlsx, .xls) or CSV files are allowed'));
      event.target.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      storeDispatch(showErrorSnackbar('Error reading file'));
      event.target.value = '';
    };

    reader.onload = (loadEvent: any) => {
      try {
        const data = loadEvent.target.result;
        const workbook = xlsx.read(data, { type: 'array' });

        if (!workbook.SheetNames.length) {
          storeDispatch(showErrorSnackbar('This file has no sheet data'));
          return;
        }

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const headerRow: string[] = (xlsx.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[]) || [];

        if (!headerRow.length) {
          storeDispatch(showErrorSnackbar('No column headers found in this file'));
          return;
        }

        const invalidHeaders = headerRow.filter((header) => !ALLOWED_HEADERS.includes(header));
        if (invalidHeaders.length) {
          storeDispatch(
            showErrorSnackbar(
              `Invalid column header(s): ${invalidHeaders.join(', ')}. Column headers must exactly match (case-sensitive): ${ALLOWED_HEADERS.join(', ')}`
            )
          );
          return;
        }

        const duplicateHeaders = Array.from(new Set(headerRow.filter((header, index) => headerRow.indexOf(header) !== index)));
        if (duplicateHeaders.length) {
          storeDispatch(showErrorSnackbar(`Duplicate column header(s): ${duplicateHeaders.join(', ')}`));
          return;
        }

        if (!headerRow.includes('suite')) {
          storeDispatch(showErrorSnackbar('A "suite" column is required'));
          return;
        }

        const rows = xlsx.utils.sheet_to_json(worksheet) as any[];
        if (!rows.length) {
          storeDispatch(showErrorSnackbar('No data rows found in this file'));
          return;
        }

        const unmatchedSuites: string[] = [];
        const invalidRows: string[] = [];

        const unitArray = rows
          .map((row: any) => {
            const suite = hasValue(row.suite) ? row.suite.toString().trim() : '';
            if (!suite) return null;

            const existingUnit = units.find((unit: IUnit) => unit.suite === suite);
            if (!existingUnit) {
              unmatchedSuites.push(suite);
              return null;
            }

            const invalidFields: string[] = [];
            const parseOptionalNumber = (raw: any, fallback: number, label: string) => {
              if (!hasValue(raw)) return fallback;
              const parsed = Number(raw);
              if (!Number.isFinite(parsed)) {
                invalidFields.push(`${label} ("${raw}")`);
                return fallback;
              }
              return parsed;
            };

            let basePrice = existingUnit.basePrice;
            let basePriceFromNetHst = false;
            if (hasValue(row.netHstPrice)) {
              const netValue = Number(row.netHstPrice);
              if (!Number.isFinite(netValue)) {
                invalidFields.push(`netHstPrice ("${row.netHstPrice}")`);
              } else {
                basePrice = Math.round(grossPriceFromNetHST(netValue) * 100) / 100;
                basePriceFromNetHst = true;
              }
            } else if (hasValue(row.basePrice)) {
              const rawBasePrice = Number(row.basePrice);
              if (!Number.isFinite(rawBasePrice)) {
                invalidFields.push(`basePrice ("${row.basePrice}")`);
              } else {
                basePrice = Math.round(rawBasePrice * 100) / 100;
              }
            }

            const unitType = hasValue(row.unitType) ? row.unitType.toString() : existingUnit.unitType;
            const modelType = hasValue(row.modelType) ? row.modelType.toString() : existingUnit.modelType;
            const size = parseOptionalNumber(row.size, existingUnit.size, 'size');
            const outdoorSize = parseOptionalNumber(row.outdoorSize, getOutdoorSize(existingUnit.outdoor), 'outdoorSize');
            const exposure = hasValue(row.exposure) ? row.exposure.toString() : existingUnit.exposure;
            const bathroom = parseOptionalNumber(row.bathroom, existingUnit.bathroom, 'bathroom');
            const rental = parseOptionalNumber(row.rental, existingUnit.rental, 'rental');

            if (invalidFields.length) {
              invalidRows.push(`Suite ${suite}: ${invalidFields.join(', ')}`);
              return null;
            }

            const historyType: string[] = [];
            const historyDescription: string[] = [];
            let priceFrom: number | undefined;
            let priceTo: number | undefined;

            if (basePrice !== existingUnit.basePrice) {
              historyType.push('Price');
              historyDescription.push(
                `Price has been changed from ${numToCurrency.format(existingUnit.basePrice)} to ${numToCurrency.format(basePrice)}${
                  basePriceFromNetHst ? ' (calculated from uploaded Net HST Price)' : ''
                }`
              );
              // NewHistory.priceFrom/priceTo are typed Int on the backend, unlike basePrice itself
              // (Float) - round to whole dollars or the mutation gets rejected for non-integer values.
              priceFrom = Math.round(existingUnit.basePrice);
              priceTo = Math.round(basePrice);
            }
            if (unitType !== existingUnit.unitType) {
              historyType.push('Unit Type');
              historyDescription.push(`Unit Type has been changed from ${existingUnit.unitType} to ${unitType}`);
            }
            if (modelType !== existingUnit.modelType) {
              historyType.push('Model Type');
              historyDescription.push(`Model Type has been changed from ${existingUnit.modelType} to ${modelType}`);
            }
            if (size !== existingUnit.size) {
              historyType.push('Size');
              historyDescription.push(`Size has been changed from ${existingUnit.size} to ${size}`);
            }
            if (outdoorSize !== getOutdoorSize(existingUnit.outdoor)) {
              historyType.push('Outdoor Size');
              historyDescription.push(`Outdoor Size has been changed from ${getOutdoorSize(existingUnit.outdoor)} to ${outdoorSize}`);
            }
            if (exposure !== existingUnit.exposure) {
              historyType.push('Exposure');
              historyDescription.push(`Exposure has been changed from ${existingUnit.exposure} to ${exposure}`);
            }
            if (bathroom !== existingUnit.bathroom) {
              historyType.push('Bathroom');
              historyDescription.push(`Bathroom has been changed from ${existingUnit.bathroom} to ${bathroom}`);
            }
            if (rental !== existingUnit.rental) {
              historyType.push('Rental');
              historyDescription.push(
                `Rental has been changed from ${existingUnit.rental ? numToCurrency.format(existingUnit.rental) : 'None'} to ${
                  rental ? numToCurrency.format(rental) : 'None'
                }`
              );
            }

            const updatedUnit: any = {
              _id: existingUnit._id,
              basePrice,
              unitType,
              modelType,
              size,
              outdoor: [{ name: getOutdoorType(existingUnit.outdoor), size: outdoorSize }],
              exposure,
              bathroom,
              rental,
              removeAllocations: false,
            };

            if (historyType.length) {
              updatedUnit.history = {
                type: historyType.join(', '),
                description: historyDescription.join(', '),
                timestamp: new Date(),
                user: user._id,
                priceFrom,
                priceTo,
              };
            }

            return updatedUnit;
          })
          .filter((unit): unit is NonNullable<typeof unit> => unit !== null);

        if (unmatchedSuites.length) {
          storeDispatch(showErrorSnackbar(`No matching unit found for suite(s): ${unmatchedSuites.join(', ')}`));
        }

        if (invalidRows.length) {
          storeDispatch(showErrorSnackbar(`Skipped row(s) with invalid numeric value(s) - ${invalidRows.join('; ')}`));
        }

        if (!unitArray.length) {
          if (!unmatchedSuites.length && !invalidRows.length) {
            storeDispatch(showErrorSnackbar('No rows with a suite found in this file'));
          }
          return;
        }

        updateUnits({ variables: { unitArray, project: project._id } });
      } catch (err) {
        console.log(err, 'err');
        storeDispatch(showErrorSnackbar('Unable to read this file. Please make sure it is a valid Excel or CSV file.'));
      } finally {
        event.target.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <Box>
      <Typography sx={{ mb: 2 }} variant={'h5'}>
        <strong>Upload Excel</strong>
      </Typography>
      <Box sx={{ my: 2 }}>The following unit details can be changed and all columns with the headers should be displayed exactly like below.</Box>
      <Box>
        suite*
      </Box>
      <Box>
        unitType
      </Box>
      <Box>
        modelType
      </Box>
      <Box>
        size
      </Box>
      <Box>
        outdoorSize
      </Box>
      <Box>
        exposure
      </Box>
      <Box>
        basePrice (This is the gross price)
      </Box>
      <Box>
        bathroom
      </Box>
      <Box>
        rental
      </Box>
      <Box>
        netHstPrice (This will update the gross price of this number on to the app using the net hst price)
      </Box>
      <FlexEnd sx={{ mt: 2 }}>
        <Button variant="contained" color="info" sx={{ mr: 1 }} onClick={handleClose}>
          Close
        </Button>
        <input
          style={{ display: 'none' }}
          id="upload-excel-file"
          name="upload-excel-file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => handleFile(e)}
          type="file"
        />
        <label htmlFor="upload-excel-file">
          <Button color="primary" variant="contained" component="span" disabled={loading}>
            {loading ? 'Uploading...' : 'Upload File'}
          </Button>
        </label>
      </FlexEnd>
    </Box>
  );
};

const UPDATEUNITS = gql`
  mutation unitUpdateManyInfo($unitArray: [NewUnitInput!], $project: MongoID!) {
    unitUpdateManyInfo(unitArray: $unitArray, project: $project) {
      _id
      basePrice
      modelType
      unitType
      size
      exposure
      bathroom
      outdoor {
        name
        size
      }
      rental
    }
  }
`;

export default UploadExcel;
